import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Button from '@/shared/Button'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'

import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'

import AddCircle from '@mui/icons-material/AddCircle'
import Edit from '@mui/icons-material/Edit'
import Groups from '@mui/icons-material/Groups'
import Close from '@mui/icons-material/HighlightOff'
import PlaylistAddOutlined from '@mui/icons-material/PlaylistAddOutlined'

import CreateRegisteredUserDialog from '@/components/CreateRegisteredUserDialog'
import EditRegisteredUserDialog from '@/components/EditRegisteredUserDialog'
import EditUserGroupDialog from '@/components/EditUserGroupDialog'
import UploadListDialog from '@/components/UploadListDialog'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import Typography from '@mui/material/Typography'
import get from 'lodash/get'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Watermark from '../components/Watermark/Watermark'
import generateUsername from '../components/Watermark/GenerateUsername'

const mapStateToProps = ({ user, loading }) => ({
  userList: user.userList,
  isLoading: loading['user/getUsers'],
  deleteLoading: loading['user/deleteUser'],
})

const fieldMap = {
  userGroups: 'USER_GROUP',
  companyName: 'COMP_NAME',
  email: 'EMAIL',
  lastName: 'LAST_NAME',
  firstName: 'FIRST_NAME',
  cname: 'CNAME',
  deptGroup: 'DEPT_GROUP',
  dept: 'DEPT',
  position: 'POSITION',
  manager: 'MANAGER',
  joinOn: 'JOINON',
  phone: 'PHONE',
}

const UserGroupDetails = ({ dispatch, userList, isLoading, deleteLoading }) => {
  const { data, paging = {} } = userList || {}

  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const [sort, setSort] = useState([{ field: 'userGroup', sort: 'asc' }])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteUserData, setDeleteUserData] = useState()
  const [editUserData, setEditUserData] = useState()
  const [editUserGroupData, setEditUserGroupData] = useState()
  const [showUpload, setShowUpload] = useState(false)
  const [userGroupData, setUserGroupData] = useState({})

  const companyId = useSelector(state => state.auth.account?.companyId)

  const accountEmail = useSelector(state => state.auth.account?.accountEmail)

  const employeeId = useSelector(state => state.auth.account?.employeeId)

  const username = generateUsername(employeeId, accountEmail)

  const params = useParams()
  const { userGroupId } = params

  const form = useRef()

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const btnProps = {
    size: 'small',
    sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
  }

  const iconProps = {
    sx: {
      padding: 0.5,
    },
  }

  const cols = [
    {
      field: 'userGroups',
      headerName: t('general.user_group_name'),
      width: 130,
    },
    {
      field: 'companyName',
      headerName: t('general.company_name'),
      width: 130,
    },
    {
      field: 'email',
      headerName: t('general.email'),
      width: 200,
    },
    {
      field: 'phone',
      headerName: t('general.phone'),
      width: 200,
    },
    {
      field: 'lastName',
      headerName: t('general.last_name'),
      width: 80,
    },
    {
      field: 'firstName',
      headerName: t('general.first_name'),
      width: 80,
    },
    {
      field: 'cname',
      headerName: t('general.name'),
      width: 120,
    },
    {
      field: 'deptGroup',
      headerName: t('general.business_group'),
      width: 120,
    },
    {
      field: 'dept',
      headerName: t('general.department'),
      width: 120,
    },
    {
      field: 'position',
      headerName: t('general.position'),
      width: 120,
    },
    {
      field: 'manager',
      headerName: t('general.manager'),
      width: 120,
    },
    {
      field: 'joinOn',
      headerName: t('general.created_date'),
      width: 200,
      valueFormatter: ({ value }) => (value ? moment(value).format(DATE_TIME_FORMAT) : '-'),
    },
    {
      field: 'edit',
      headerName: t('general.edit'),
      width: 100,
      renderCell: record => (
        <IconButton
          {...btnProps}
          onClick={() => {
            setEditUserData(record.row)
          }}
        >
          <Edit {...iconProps} />
        </IconButton>
      ),
    },
    {
      field: 'delete',
      headerName: t('general.delete'),
      width: 100,
      renderCell: record => (
        <IconButton
          {...btnProps}
          onClick={() => {
            setDeleteUserData(record.row)
          }}
        >
          <Close {...iconProps} />
        </IconButton>
      ),
    },
    {
      field: 'manage',
      headerName: t('general.manage_group'),
      width: 130,
      renderCell: record => (
        <IconButton
          {...btnProps}
          onClick={() => {
            setEditUserGroupData(record.row)
          }}
        >
          <Groups {...iconProps} />
        </IconButton>
      ),
    },
  ]

  const fetchData = useCallback(
    () => {
      const formValues = form.current?.getValues()
      const { search, userGroup } = formValues || {}
      const { id } = userGroup || {}

      let payload = {
        searchParam: search,
        userGroupId,
        sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
        pageIndex: page,
        pageSize,
      }

      if (search) {
        payload = { ...payload, searchParam: search }
      }

      if (id) {
        payload = { ...payload, userGroupId: id }
      }

      dispatch({
        type: 'user/getUsers',
        payload,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort, page, pageSize, form],
  )

  const deleteUser = () => {
    if (deleteUserData) {
      dispatch({
        type: 'user/deleteUser',
        payload: { userId: deleteUserData.userId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.email_deleted', { email: deleteUserData.email }),
              severity: 'success',
            },
          })
          setDeleteUserData()
          fetchData()
        },
      })
    }
  }

  useEffect(() => {
    dispatch({
      type: 'userGroup/getUserGroup',
      successCallback: response => {
        const { groupId, groupName } = response

        navigate(pathname, { state: { name: groupName, reset: false }, replace: true })
        setUserGroupData({ id: groupId, name: groupName })
      },
      payload: { groupId: userGroupId },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, page, pageSize, fetchData])

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2} my={1}>
        <Grid item mr={2}>
          <Button
            variant="text"
            color="secondary_80"
            endIcon={<AddCircle sx={{ color: '#2D2D6C80' }} />}
            onClick={() => {
              setCreateDialogOpen(true)
            }}
          >
            {t('general.add_registered_user')}
          </Button>
        </Grid>
        <Grid item mr={2}>
          <Button
            variant="text"
            color="secondary_80"
            endIcon={<PlaylistAddOutlined sx={{ color: '#2D2D6C80' }} />}
            onClick={() => {
              setShowUpload(true)
            }}
          >
            {t('general.upload_name_batch')}
          </Button>
        </Grid>
        <Grid item>
          <FormCreator
            ref={form}
            formItems={[
              {
                name: 'search',
                placeholder: t('general.insert_keyword'),
                defaultValue: '',
                inputConfig: {
                  onSubmit: async () => {
                    setPage(0)
                    fetchData()
                  },
                },
              },
            ]}
            flexDirection="row"
            submitComponent={() => null}
            resetComponent={() => null}
            gridItemProps={{ mr: 2 }}
          />
        </Grid>
        <Grid item>
          <Button
            variant="text"
            color="secondary_80"
            endIcon={<Close sx={{ color: '#2D2D6C80', stroke: '#2D2D6C80', strokeWidth: 2 }} />}
            onClick={() => {
              form.current?.setValue?.('search', '')
              form.current?.setValue?.('userGroup', '')
              fetchData()
            }}
          >
            {t('general.clear_conditions')}
          </Button>
        </Grid>
      </Grid>
      <div style={{ position: 'relative' }}>
        <Table
          minHeight={400}
          columns={cols}
          rows={data || []}
          dataGridConfig={{
            sortingMode: 'server',
            sortingOrder: ['desc', 'asc', null],
            sortModel: sort,
            onSortModelChange: newSort => setSort(newSort),
            getRowId: row => row.userId,
            loading: isLoading,
          }}
          noRowsLabel={t('errors.no_registered_users')}
          pagination={pagination}
        />
        {companyId === 1 && <Watermark username={username} higher={true} />}
      </div>

      <CreateRegisteredUserDialog
        userGroup={userGroupData}
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false)
        }}
        onComplete={() => {
          fetchData()
        }}
      />

      <EditRegisteredUserDialog
        data={editUserData}
        onClose={() => {
          setEditUserData()
        }}
        onComplete={() => {
          fetchData()
        }}
      />

      <EditUserGroupDialog
        data={editUserGroupData}
        onClose={() => {
          setEditUserGroupData()
        }}
        onComplete={() => {
          fetchData()
        }}
      />

      <UploadListDialog
        userGroupName={get(userGroupData, 'name')}
        open={showUpload}
        onClose={() => {
          setShowUpload(false)
        }}
        onComplete={() => {
          fetchData()
        }}
      />

      <ConfirmDialog
        open={!!deleteUserData}
        onClose={() => {
          setDeleteUserData()
        }}
        onSubmit={() => {
          deleteUser()
        }}
        loading={deleteLoading}
      >
        <Typography>
          {t('messages.confirm_delete_email', { email: deleteUserData?.email })}
        </Typography>
      </ConfirmDialog>
    </Container>
  )
}

export default connect(mapStateToProps)(UserGroupDetails)
